var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AvatarFacet, CreateSubModelMessage, UpdateFacetOnModelMessage, checkMatrix4Near, RenderVisibilityMessage, calcStringByteLength, worldToLocalTransform, worldToLocalPosition, } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { RequestRoomTransformMessage, RoomTransformMessage } from "@messages/scenegraph/ScenegraphMessages";
import { DeleteMarkerMessage, CreateMarkerMessage, UpdateMarkerTargetMessage } from "@messages/rendering/MeshMessages";
import { AvatarMeshVisibilityMessage, AvatarPeerRoomChangedMessage } from "@messages/session/PeerMessages";
import { SessionRemoveAvatarObject, SessionCreateAvatarObject } from "@messages/session/SessionMessages";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { createModelFromPath, getModelFromAbsolutePath, modelToAbsolutePath, modelPool, determineModelNameForPrinting, computeLocalMatrixFromWorldMatrix, NO_MESSAGING, useExternalMessages } from "@modules/model";
import { ensurePlayerHeadModel, ensurePlayerRootModel } from "@modules/player";
import { NetworkObject, NetworkObjectType } from "./NetworkObject";
import { remoteStreamingService } from "./RemoteStreamingService";
import { TransformHistory } from "./TransformHistory";
import { usersService } from "../UsersService";
const INTERPOLATE_DELAY = 80;
const AVATAR_TRANSFORM_EPSILON = 1e-5;
const tmpAvatarTransform = new Matrix4();
const tmpAvatarLocalTransform = new Matrix4();
const tmpMarkerWorldPosition = new Vector3();
const tmpMarkerLocalPosition = new Vector3();
const tmpAvatarLocalScale = new Vector3();
/**
 * Creates the model locally. Doesn't broadcast the model creation. You need to send the CreateSubModelMessage
 * yourself.
 */
export function generateAvatarModelIds(clientIdx) {
    const avatarRoot = ensurePlayerRootModel(clientIdx);
    const avatarHead = ensurePlayerHeadModel(clientIdx);
    return {
        rootUid: avatarRoot.getUID(),
        headUid: avatarHead.getUID()
    };
}
export class AvatarPeer extends NetworkObject {
    get constructorByteLength() {
        return (this.byteLength + // position, orientation, scale
            calcStringByteLength(this.getCurrentRoomPath()) // roomPathOfModel
        );
    }
    constructor(mgr, ownerId, netUid) {
        super(ownerId, netUid);
        this.mgr = mgr;
        this.byteLength = Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 1);
        this.transformHistory = new TransformHistory();
        this.latestServerTimestamp = 0;
        this.position = new Vector3();
        this.orientation = new Quaternion();
        this.scale = new Vector3(1, 1, 1);
        this.isVisible = true;
        this.isMuted = false;
        this.roomMatrix = new Matrix4();
        this._updateMarkerTargetMessage = new UpdateMarkerTargetMessage(0, [0, 0, 0], 0);
        this.lastAvatarWorldMatrix = new Matrix4();
        this.lastMarkerTargetPosition = new Vector3(Number.NaN, Number.NaN, Number.NaN);
        this.hasAppliedAvatarWorldMatrix = false;
        this.waitForRoomTransform = false;
        this.isRoomCreationFinished = false;
        this.onUserChanged = (user) => {
            if (user.clientId !== this.ownerId)
                return;
            this.updateVisualisation(user);
        };
        this.pendingMessages = mgr.pendingBusMessages;
        usersService.onUserAddedDelegate.add(this.onUserChanged);
    }
    getClientId() {
        return this.ownerId;
    }
    setLocalVisible(visible) {
        if (visible === this.isVisible)
            return;
        this.isVisible = visible;
        if (this.uid === undefined)
            return;
        this.pendingMessages.push(new RenderVisibilityMessage(this.uid, visible));
    }
    onRemoteCreate() {
        this.mgr.addAvatar(this.ownerId, this);
        if (this.uid === undefined || this.currentRoomUID === undefined)
            return;
        this.pendingMessages.push(new SessionCreateAvatarObject(this.ownerId, this.uid, this.currentRoomUID));
        this.mgr.peerAvatarCreatedDelegate.invoke(this);
    }
    onRemoteRemove() {
        if (this.mgr.removeAvatar(this.ownerId)) {
            this.mgr.peerAvatarRemovedDelegate.invoke(this);
        }
        if (this.uid !== undefined) {
            this.pendingMessages.push(new DeleteMarkerMessage(this.uid), new SessionRemoveAvatarObject(this.ownerId, this.uid));
        }
    }
    destroy() {
        usersService.onUserAddedDelegate.remove(this.onUserChanged);
        this.mgr.removeAvatar(this.ownerId);
    }
    destroyFromClient() {
        this.destroy();
    }
    onRemoteRoomChanged(roomUID) {
        if (this.currentRoomUID && this.currentRoomUID === roomUID) {
            this.syncAvatarVisibilityState();
            if (this.isVisible && (this.isRoomTransformInvalid || !this.isRoomCreationFinished) && !this.waitForRoomTransform) {
                void this.requestRoomTransform();
            }
            return;
        }
        this.currentRoomUID = roomUID;
        this.isRoomCreationFinished = false;
        this.isRoomTransformInvalid = undefined;
        this.waitingRoomChange = undefined;
        const isVisible = this.mgr.isModelVisibleForPeer(this.currentRoomUID);
        this.setLocalVisible(isVisible);
        this.syncAvatarVisibilityState();
        if (this.uid !== undefined) {
            this.pendingMessages.push(new AvatarPeerRoomChangedMessage(this.uid, this.mgr.getRoomDepthDiffToPeer(this.currentRoomUID), this.mgr.isPeerAudibleForCurrentRoom(this.currentRoomUID)));
        }
        if (isVisible) {
            void this.requestRoomTransform();
        }
    }
    requestRoomTransform() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.currentRoomUID) {
                return;
            }
            this.waitForRoomTransform = true;
            this.syncAvatarVisibilityState();
            try {
                const response = yield this.mgr.connection.askByMessageType(new RequestRoomTransformMessage(this.currentRoomUID), RoomTransformMessage);
                if (this.isRoomTransformInvalid) {
                    this.isRoomTransformInvalid = undefined;
                }
                this.roomMatrix.fromArray(response.worldMatrix);
                this.isRoomCreationFinished = response.layoutCreationFinished;
                this.waitingRoomChange = this.latestServerTimestamp;
            }
            catch (error) {
                this.isRoomTransformInvalid = true;
                this.isRoomCreationFinished = false;
                const roomName = determineModelNameForPrinting(this.currentRoomUID);
                console.warn(`[multiplayer] Can't get transform for room ${roomName}, avatar is hidden`);
                if (this.resendRoomTransform) {
                    this.resendRoomTransformRequest();
                }
            }
            finally {
                this.waitForRoomTransform = false;
                this.syncAvatarVisibilityState();
            }
        });
    }
    resendRoomTransformRequest() {
        if (!this.currentRoomUID) {
            return;
        }
        this.resendRoomTransform = undefined;
        const room = this.currentRoomUID;
        this.currentRoomUID = undefined;
        this.onRemoteRoomChanged(room);
    }
    onClientRoomChanged() {
        if (!this.currentRoomUID)
            return;
        const isVisible = this.mgr.isModelVisibleForPeer(this.currentRoomUID);
        this.setLocalVisible(isVisible);
        this.syncAvatarVisibilityState();
        if (isVisible && !this.waitForRoomTransform) {
            void this.requestRoomTransform();
        }
        if (this.uid !== undefined) {
            this.pendingMessages.push(new AvatarPeerRoomChangedMessage(this.uid, this.mgr.getRoomDepthDiffToPeer(this.currentRoomUID), this.mgr.isPeerAudibleForCurrentRoom(this.currentRoomUID)));
        }
    }
    onRoomTransformUpdate(matrix) {
        this.currentRoomUID && this.setLocalVisible(this.mgr.isModelVisibleForPeer(this.currentRoomUID));
        this.roomMatrix.fromArray(matrix);
        if (!this.isRoomCreationFinished) {
            this.isRoomCreationFinished = true;
        }
        this.isRoomTransformInvalid = undefined;
        this.syncAvatarVisibilityState();
    }
    onLayouterCreationFinished(uid) {
        if (!this.isRoomCreationFinished) {
            this.isRoomCreationFinished = true;
        }
        this.syncAvatarVisibilityState();
        if (!this.currentRoomUID || this.currentRoomUID !== uid) {
            return;
        }
        if (this.waitForRoomTransform) {
            this.resendRoomTransform = true;
        }
        else if (this.isRoomTransformInvalid) {
            this.resendRoomTransformRequest();
        }
    }
    constructorSerialization(stream) {
        const transState = this.transformSerialization(stream);
        const roomPathOfModel = this.getCurrentRoomPath();
        const roomPath = stream.utf16String(roomPathOfModel);
        if (stream.isReading) {
            this.initRemote(transState, roomPath);
        }
    }
    initRemote(transState, roomPath) {
        const user = usersService.getUserByClientId(this.ownerId);
        if (!user) {
            throw new Error(`No user ${this.ownerId} set`);
        }
        this.position.copy(transState.position);
        this.orientation.copy(transState.rotation);
        this.scale.copy(transState.scale);
        let room = getModelFromAbsolutePath(roomPath);
        if (room === undefined) {
            room = createModelFromPath(roomPath);
        }
        const avatarModelIds = generateAvatarModelIds(this.mgr.getClientIdxOrThrow(this.ownerId));
        this.rootUid = avatarModelIds.rootUid;
        this.uid = avatarModelIds.headUid;
        usersService.setUserAvatarUid(this.getClientId(), this.uid);
        tmpAvatarTransform.compose(this.position, this.orientation, this.scale);
        tmpAvatarTransform.premultiply(this.roomMatrix);
        const avatarRoot = modelPool.getModelOrThrow(this.rootUid);
        const avatarHead = modelPool.getModelOrThrow(this.uid);
        const rootCreationMessage = new CreateSubModelMessage(avatarRoot.getUID());
        rootCreationMessage.path = modelToAbsolutePath(avatarRoot);
        const headCreationMessage = new CreateSubModelMessage(avatarHead.getUID());
        headCreationMessage.path = modelToAbsolutePath(avatarHead);
        this.pendingMessages.push(rootCreationMessage, headCreationMessage);
        this.updateVisualisation(user);
        this.updateAvatarWorldMatrix(this.uid, tmpAvatarTransform, this.pendingMessages);
        this.onRemoteRoomChanged(room.getUID());
    }
    updateVisualisation(user) {
        if (this.uid === undefined) {
            return;
        }
        this.pendingMessages.push(new CreateMarkerMessage(this.uid, user.avatar.baseColor, user.avatar.imageUrl, false));
        this.updateAvatarVisualisation(user, {
            isMuted: this.checkIsMuted()
        });
    }
    getRootUid() {
        return this.rootUid;
    }
    serialization(stream) {
        this.transformSerialization(stream);
    }
    transformSerialization(stream) {
        const state = this.transformHistory.getFreeState();
        const roomWorldTransform = modelPool.getModelOrThrow(this.mgr.currentRoomUID).getWorldTransform();
        tmpAvatarTransform.compose(localPlayerContainer.cameraWorldPosition, localPlayerContainer.cameraWorldOrientation, tmpAvatarLocalScale.setScalar(localPlayerContainer.getPlatformScale()));
        worldToLocalTransform(tmpAvatarTransform, roomWorldTransform, tmpAvatarLocalTransform);
        tmpAvatarLocalTransform.decompose(state.position, state.rotation, tmpAvatarLocalScale);
        state.position.fromArray(stream.number3(state.position.toArray()));
        state.rotation.fromArray(stream.number4(state.rotation.toArray()));
        state.scale.setScalar(stream.float32(tmpAvatarLocalScale.x));
        if (stream.isReading) {
            this.transformHistory.addState(state);
        }
        return state;
    }
    interpolate(time) {
        if (this.uid === undefined)
            return;
        const isMuted = this.checkIsMuted();
        if (isMuted !== this.isMuted) {
            const user = usersService.getUserByClientId(this.ownerId);
            if (user) {
                this.updateAvatarVisualisation(user, {
                    isMuted
                });
            }
        }
        if (!this.transformHistory.interpolate(time, INTERPOLATE_DELAY, this.position, this.scale, this.orientation))
            return;
        if (this.waitingRoomChange) {
            if (time - INTERPOLATE_DELAY * 2 > this.waitingRoomChange) {
                this.waitingRoomChange = undefined;
            }
            else {
                return;
            }
        }
        tmpAvatarTransform.compose(this.position, this.orientation, this.scale);
        tmpAvatarTransform.premultiply(this.roomMatrix);
        if (this.hasAvatarTransformChanged(tmpAvatarTransform)) {
            this.updateAvatarWorldMatrix(this.uid, tmpAvatarTransform, this.pendingMessages);
            this.lastAvatarWorldMatrix.copy(tmpAvatarTransform);
            this.hasAppliedAvatarWorldMatrix = true;
        }
        if (this.currentRoomUID !== undefined && this.hasMarkerTargetChanged()) {
            this._updateMarkerTargetMessage.markerUid = this.uid;
            const roomWorldTransform = modelPool.getRoomOrThrow(this.currentRoomUID).getWorldTransform();
            tmpAvatarTransform.decompose(tmpMarkerWorldPosition, new Quaternion(), new Vector3());
            worldToLocalPosition(tmpMarkerWorldPosition, roomWorldTransform, tmpMarkerLocalPosition).toArray(this._updateMarkerTargetMessage.position);
            this._updateMarkerTargetMessage.roomUid = this.currentRoomUID;
            this.pendingMessages.push(this._updateMarkerTargetMessage);
            this.lastMarkerTargetPosition.copy(this.position);
            this.lastMarkerTargetRoomUID = this.currentRoomUID;
        }
    }
    hasMarkerTargetChanged() {
        if (this.currentRoomUID === undefined)
            return false;
        if (this.lastMarkerTargetRoomUID !== this.currentRoomUID)
            return true;
        return !this.position.equals(this.lastMarkerTargetPosition);
    }
    hasAvatarTransformChanged(worldMatrix) {
        if (!this.hasAppliedAvatarWorldMatrix)
            return true;
        return !checkMatrix4Near(worldMatrix, this.lastAvatarWorldMatrix, AVATAR_TRANSFORM_EPSILON);
    }
    syncAvatarVisibilityState() {
        if (this.uid === undefined)
            return;
        const shouldShowMesh = this.isVisible &&
            this.isRoomCreationFinished &&
            !this.waitForRoomTransform &&
            !this.isRoomTransformInvalid;
        if (this.lastAvatarMeshVisibility === shouldShowMesh)
            return;
        this.lastAvatarMeshVisibility = shouldShowMesh;
        this.pendingMessages.push(new AvatarMeshVisibilityMessage(this.uid, shouldShowMesh));
    }
    checkIsMuted() {
        if (remoteStreamingService.getGloballyMuted()) {
            return false;
        }
        const remoteStreams = remoteStreamingService.getMicrophoneStreams().filter((stream) => stream.clientId === this.ownerId);
        if (remoteStreams.length === 0) {
            return false;
        }
        remoteStreams.forEach((stream) => {
            if (stream.stream.getAudioTracks().some((track) => track.enabled)) {
                return false;
            }
        });
        return true;
    }
    updateAvatarVisualisation(user, params) {
        if (this.uid === undefined)
            return;
        if (params.isMuted !== undefined) {
            this.isMuted = params.isMuted;
        }
        params.accessoires = user.avatar.accessoires;
        const displayName = usersService.getDisplayName(this.ownerId);
        this.pendingMessages.push(new UpdateFacetOnModelMessage(this.uid, new AvatarFacet(user.avatar.imageUrl, user.avatar.style, user.avatar.baseColor, this.ownerId, displayName, undefined, params)));
    }
    getCurrentRoomPath() {
        const roomModel = modelPool.getModelOrThrow(this.mgr.currentRoomUID);
        return modelToAbsolutePath(roomModel);
    }
    updateAvatarWorldMatrix(avatarUID, worldMatrix, messages) {
        const model = modelPool.getModel(avatarUID);
        if (model === undefined) {
            console.warn(`[multiplayer] There is no avatar for ${determineModelNameForPrinting(avatarUID)}`);
            return;
        }
        const localMatrix = computeLocalMatrixFromWorldMatrix(model, worldMatrix);
        model.setTransform(localMatrix, useExternalMessages(messages, Object.assign(Object.assign({}, NO_MESSAGING), { updateFacet: false })));
    }
}
AvatarPeer.staticObjectType = NetworkObjectType.Avatar;
